import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';

const defaultUniqueErrorText = i18n._(t`Rule already exists`);
const defaultAddButtonText = i18n._(t`Create`);
const defaultUpdateButtonText = i18n._(t`Update`);
const createTitle = i18n._(t`Create Rule`);
const editTitle = i18n._(t`Edit Rule`);
const cancelText = i18n._(t`Cancel`);
const MAX_LENGTH = 50;
// Reproduces the portal/ui wan-config-pattern-dialog footprint (500x240
// including toolbar/footer).
const DEFAULT_CONTENT_STYLE = { width: 450, minHeight: 100 };

export default function RuleForm({
    title,
    fieldLabel = t`Rule Value`,
    fieldName,
    selectedList = [],
    selectedRecord,
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
    addButtonText = defaultAddButtonText,
    updateButtonText = defaultUpdateButtonText,
    uniqueErrorText = defaultUniqueErrorText,
    contentStyle = DEFAULT_CONTENT_STYLE,
}) {
    const defaultFormValues = useMemo(
        () => ({
            [fieldName]: '',
        }),
        [fieldName]
    );
    // Override shouldUnregister: the MUI Dialog unmounts children when closed,
    // which would unregister the Controller-backed field. That makes the
    // open-time trigger(fieldName) race with re-registration and emits
    // "Field is missing with `name` attribute" warnings. Keeping fields
    // registered for the lifetime of this form avoids the race entirely.
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
        mode: 'onChange',
        shouldUnregister: false,
    });
    const internalId = useRef(null);
    const { handleSubmit, formState, reset, trigger } = methods;
    // react-hook-form v6 does not expose `errors` through the formState
    // proxy, so a bump after trigger() is the simplest way to force a
    // re-render that lets children read the freshly-set errors.
    const [, bumpAfterTrigger] = useState(0);

    const isEdit = !_.isEmpty(selectedRecord) && !!selectedRecord[INTERNAL_ID_FIELD];
    const dialogTitle = isEdit ? editTitle : title || createTitle;
    const confirmButtonText = isEdit ? updateButtonText : addButtonText;

    // Keep the latest list in a ref so the validate closure (registered once
    // with the field) always sees fresh data without being re-created.
    const selectedListRef = useRef(selectedList);
    selectedListRef.current = selectedList;

    const validateUnique = useCallback(
        (value) => {
            const duplicate = _.find(
                selectedListRef.current,
                (item) =>
                    _.toLower(item[fieldName]) === _.toLower(value) && item[INTERNAL_ID_FIELD] !== internalId.current
            );
            return duplicate ? uniqueErrorText : true;
        },
        [fieldName, uniqueErrorText]
    );

    const fieldRules = useMemo(() => ({ validate: { unique: validateUnique } }), [validateUnique]);

    useEffect(() => {
        // Only run when the dialog transitions to open. Re-running this on
        // every render (e.g. via an unstable `selectedRecord` default) would
        // re-trigger built-in validation and wipe manual errors set by
        // handleFormSubmit (such as the duplicate-rule error).
        if (!open) return;

        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            reset(selectedRecord);
        }

        // Surface the required-field validation as soon as the dialog opens.
        // The setTimeout pushes the bump out of any batch RHF is in the
        // middle of, so the re-render reliably picks up the new errors.
        const id = window.setTimeout(() => {
            trigger(fieldName).then(() => bumpAfterTrigger((c) => c + 1));
        }, 0);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCleanup = () => {
        internalId.current = null;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId.current) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        // Uniqueness is enforced by the on-change `validate.unique` rule, so
        // the submit button is already disabled when there is a duplicate.
        await onConfirm(dataToSave);
        handleCleanup();
    };

    return (
        <NgpTwoButtonDialog
            title={dialogTitle}
            confirmButtonText={confirmButtonText}
            cancelButtonText={cancelText}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <div style={contentStyle}>
                <FormProvider {...methods}>
                    <NgpForm
                        formId={`ruleForm-${fieldName}`}
                        initialized
                        loading={false}
                        showSave={false}
                        formPadding={false}
                        showRequiredFieldsTip={false}
                        submitFunction={handleFormSubmit}
                    >
                        <NgpTextField
                            fieldName={fieldName}
                            label={fieldLabel}
                            fullWidth
                            required
                            showCharCounter
                            fieldRules={fieldRules}
                            inputProps={{ maxLength: MAX_LENGTH, autoComplete: AUTO_COMPLETE_DEFAULT }}
                        />
                    </NgpForm>
                </FormProvider>
            </div>
        </NgpTwoButtonDialog>
    );
}
